import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from 'src/shared/Services/apiService';
import type { AppNotification } from 'src/shared/Services/apiService/PlatformTypes';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  NOTIFICATIONS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Works for every role — the backend scopes rows to the caller's identity from
 *  the token, so patients, doctors, nurses and admins all use this unchanged.
 *
 *  POLLING: the backend has no push channel yet, so the badge is refreshed on a
 *  timer. Two rules keep that cheap:
 *
 *    • Polling pauses while the tab is hidden. A backgrounded tab left open
 *      overnight would otherwise fire ~500 pointless requests.
 *    • A poll is skipped while one is already in flight, so a slow response
 *      cannot stack up a queue of overlapping requests.
 */

const POLL_INTERVAL_MS = 60_000;
const DEFAULT_LIMIT = 20;

export interface UseNotificationsResult {
  notifications: AppNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRead: (notifId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications(limit: number = DEFAULT_LIMIT): UseNotificationsResult {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await apiService.getNotifications({ limit });
      if (!mounted.current) return;
      setNotifications(res?.notifications ?? []);
      setUnreadCount(res?.unreadCount ?? 0);
      setError(null);
    } catch (err: any) {
      if (!mounted.current) return;
      // A failed poll must not blank a list that is already on screen — the
      // stale rows are more useful than an empty panel. Record the error and
      // leave the data alone.
      setError(
        err?.response?.data?.error ||
        'We could not load your notifications.',
      );
    } finally {
      inFlight.current = false;
      if (mounted.current) setLoading(false);
    }
  }, [limit]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll, but only while the tab is actually visible.
  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(refresh, POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Catch up immediately on return, then resume the cadence.
        refresh();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  /**
   * Mark one row read.
   *
   * Optimistic: the row greys out instantly and the badge drops, because the
   * user has already seen it — waiting for a round trip makes the UI feel
   * broken. On failure we put the row back rather than leaving a lie on screen.
   */
  const markRead = useCallback(async (notifId: string) => {
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.notifId !== notifId) return n;
        wasUnread = n.unread;
        return { ...n, unread: false };
      }),
    );
    if (wasUnread) setUnreadCount((c) => Math.max(0, c - 1));

    try {
      await apiService.markNotificationRead(notifId);
    } catch {
      if (!mounted.current) return;
      setNotifications((prev) =>
        prev.map((n) => (n.notifId === notifId ? { ...n, unread: wasUnread } : n)),
      );
      if (wasUnread) setUnreadCount((c) => c + 1);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    const snapshot = notifications;
    const snapshotCount = unreadCount;

    setNotifications((prev) => prev.map((n) => ({ ...n, unread: false })));
    setUnreadCount(0);

    try {
      await apiService.markAllNotificationsRead();
    } catch {
      if (!mounted.current) return;
      setNotifications(snapshot);
      setUnreadCount(snapshotCount);
    }
  }, [notifications, unreadCount]);

  return { notifications, unreadCount, loading, error, refresh, markRead, markAllRead };
}
